// @ts-nocheck — verbatim port from xop-edge-staff. See src/crm/README.md.
import React from "react"
import { Box, Card, CardContent, Typography } from "@mui/material"
import { useTheme } from "@mui/material/styles"
import { type LucideIcon } from "lucide-react"

export interface StatsCardProps {
  title: string
  value: string | number
  icon?: LucideIcon
  iconColor?: string
  valueColor?: string
  customIcon?: React.ReactNode
  sx?: import("@mui/material").SxProps<import("@mui/material").Theme>
}

// Eight gradient choices, seeded deterministically by the card title so
// the top-border color is stable across re-renders. Math.random() used
// to run on every render, which made each card's stripe flicker through
// every color whenever React redrew the list — distracting and unfit
// for a static stat. (Was Tailwind `bg-gradient-to-r from-X to-Y`; baked
// to literal CSS gradients here so the look is unchanged after the MUI
// migration drops Tailwind.)
const COLOR_LINES = [
  "linear-gradient(to right, #3b82f6, #a855f7)", // blue → purple
  "linear-gradient(to right, #22c55e, #14b8a6)", // green → teal
  "linear-gradient(to right, #f97316, #ef4444)", // orange → red
  "linear-gradient(to right, #a855f7, #ec4899)", // purple → pink
  "linear-gradient(to right, #6366f1, #3b82f6)", // indigo → blue
  "linear-gradient(to right, #10b981, #06b6d4)", // emerald → cyan
  "linear-gradient(to right, #f43f5e, #f97316)", // rose → orange
  "linear-gradient(to right, #8b5cf6, #a855f7)", // violet → purple
]

// The CRM passes stat-card icon/value colors as Tailwind text-color class
// strings (decorative, varied — not semantic palette roles). Baked to the
// exact sRGB hex the utilities produced so the 16 call sites stay unchanged
// after Tailwind is removed. Unknown values fall through (allows a raw color).
const TW_TEXT_COLOR: Record<string, string> = {
  "text-blue-600": "#2563eb",
  "text-blue-500": "#3b82f6",
  "text-green-600": "#16a34a",
  "text-green-500": "#22c55e",
  "text-emerald-600": "#059669",
  "text-purple-600": "#9333ea",
  "text-indigo-500": "#6366f1",
  "text-red-600": "#dc2626",
  "text-red-500": "#ef4444",
  "text-orange-600": "#ea580c",
  "text-amber-600": "#d97706",
  "text-yellow-600": "#ca8a04",
  "text-yellow-500": "#eab308",
  "text-gray-900": "#111827",
  "text-gray-600": "#4b5563",
}

/**
 * The same ramp two steps lighter, for the dark surface (spec D §7.1).
 *
 * The map above is Tailwind's `-600` family, chosen for a WHITE card. On a
 * dark one those are the wrong end of the ramp: `#2563eb` on `#151b23` is
 * roughly 2.6:1, well under AA for the 26px icon it colours, and
 * `text-gray-900` (`#111827`) is very nearly the dark card's own background —
 * the VALUE, the one number the card exists to show, would have vanished.
 *
 * Lifting to the `-400` shades rather than re-tinting each call site keeps
 * the 16 callers untouched and keeps one card blue in both modes. The two
 * grey entries are deliberately absent: they are not decoration, they are
 * "ink", and #resolveInk below routes them to palette roles instead.
 */
const TW_TEXT_COLOR_DARK: Record<string, string> = {
  "text-blue-600": "#60a5fa",
  "text-blue-500": "#93c5fd",
  "text-green-600": "#4ade80",
  "text-green-500": "#86efac",
  "text-emerald-600": "#34d399",
  "text-purple-600": "#c084fc",
  "text-indigo-500": "#a5b4fc",
  "text-red-600": "#f87171",
  "text-red-500": "#fca5a5",
  "text-orange-600": "#fb923c",
  "text-amber-600": "#fbbf24",
  "text-yellow-600": "#facc15",
  "text-yellow-500": "#fde047",
}

/** The two entries that are ink rather than accent. A caller asking for
 * `text-gray-900` means "the primary text colour", which is a different
 * colour in each mode — so it resolves to the palette role, not to a hex. */
const TW_INK_ROLE: Record<string, "text.primary" | "text.secondary"> = {
  "text-gray-900": "text.primary",
  "text-gray-600": "text.secondary",
}

function resolveColor(c: string | undefined, fallback: string, isDark: boolean): string {
  if (!c) return fallback
  if (isDark) return TW_TEXT_COLOR_DARK[c] ?? TW_TEXT_COLOR[c] ?? c
  return TW_TEXT_COLOR[c] ?? c
}

/** A value/ink colour as an `sx` value: a palette role when the caller named
 * one of the greys, otherwise the resolved accent hex. */
function resolveInk(c: string | undefined, isDark: boolean): string {
  if (c && TW_INK_ROLE[c]) return TW_INK_ROLE[c]
  return resolveColor(c, "text.primary", isDark)
}

function hashString(s: string): number {
  let h = 0
  for (let i = 0; i < s.length; i++) {
    h = (h * 31 + s.charCodeAt(i)) | 0
  }
  return Math.abs(h)
}

export function StatsCard({
  title,
  value,
  icon: Icon,
  iconColor = "text-blue-600",
  valueColor = "text-gray-900",
  customIcon,
  sx,
}: StatsCardProps) {
  const colorLine = COLOR_LINES[hashString(title) % COLOR_LINES.length]
  // The gradient stripe is NOT mode-switched: it is 4px of saturated
  // decoration that reads on either surface, and it is the one thing making
  // a card identifiable at a glance.
  const isDark = useTheme().palette.mode === "dark"

  return (
    <Card sx={{ position: "relative", overflow: "hidden", height: "100%", ...sx }}>
      {/* Stable top border — color is derived from the card title via
          hashString() so it stays the same across re-renders. Inline
          style (not sx) so the computed gradient is readable in tests. */}
      <Box data-testid="stats-stripe" sx={{ position: "absolute", top: 0, left: 0, right: 0, height: 4 }} style={{ background: colorLine }} />

      {/* Real vertical padding so the label clears the 4px stripe and the
          value clears the bottom edge. Was `py: 0` + `maxHeight: 76`, which
          crammed the text flush against both edges and capped the height. */}
      <CardContent sx={{ px: 2, py: 1.75, height: "100%", "&:last-child": { pb: 1.75 } }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, height: "100%" }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: "0.8125rem", fontWeight: 500, lineHeight: 1.4, color: "text.secondary" }}>{title}</Typography>
            <Typography sx={{ fontSize: "1.375rem", fontWeight: 700, lineHeight: 1.35, color: resolveInk(valueColor, isDark) }}>{value}</Typography>
          </Box>
          {customIcon && customIcon}
          {Icon && !customIcon && <Icon size={26} color={resolveColor(iconColor, isDark ? "#60a5fa" : "#2563eb", isDark)} />}
        </Box>
      </CardContent>
    </Card>
  )
}

// Colored dot icon component for consistency
export function ColoredDot({ color }: { color: string }) {
  // `color` is a Tailwind bg-* class string from callers; bake to hex.
  const TW_BG: Record<string, string> = {
    "bg-blue-100": "#dbeafe",
    "bg-green-100": "#dcfce7",
    "bg-red-100": "#fee2e2",
    "bg-yellow-100": "#fef9c3",
    "bg-purple-100": "#f3e8ff",
    "bg-orange-100": "#ffedd5",
    "bg-gray-100": "#f3f4f6",
  }
  const bg = TW_BG[color] ?? color
  return (
    <Box sx={{ width: 28, height: 28, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }} style={{ backgroundColor: bg }}>
      <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: "currentColor" }} />
    </Box>
  )
}

// Dollar icon component for consistency
export function DollarIcon() {
  return (
    <Box sx={{ width: 28, height: 28, borderRadius: "50%", bgcolor: "#f3f4f6", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <Typography component="span" sx={{ fontSize: "0.875rem", fontWeight: 700, color: "#4b5563" }}>$</Typography>
    </Box>
  )
}
